import { isTradePlacerType } from '@/features/backtest/schemas/compound'
import type { Edge, Node } from '@xyflow/react'

/** Upstream-feeder graying: nodes/edges that only feed disabled merge inputs. */
export interface UpstreamDisableResult {
  disabledNodeIds: Set<string>
  disabledEdgeIds: Set<string>
}

/**
 * Downstream "dead merge" signals: a merge ("And/Or Gate") whose every connected
 * input is disabled produces a meaningless output. We surface the merge itself
 * (greyed), its output edge(s) (drawn subtle-red), and the consumer input rows
 * fed by those edges (struck through). Keyed `${targetNodeId}::${targetHandle}`.
 */
export interface DeadMergeResult {
  deadMergeNodeIds: Set<string>
  deadOutputEdgeIds: Set<string>
  deadInputTargets: Set<string>
}

export interface DisabledUpstreamResult extends UpstreamDisableResult, DeadMergeResult {}

export function findDisabledUpstreamNodes(
  nodes: Node[],
  edges: Edge[],
  mergeNodeId: string,
  disabledInputHandles: string[],
): UpstreamDisableResult {
  const disabledNodeIds = new Set<string>()
  const disabledEdgeIds = new Set<string>()

  if (disabledInputHandles.length === 0) {
    return { disabledNodeIds, disabledEdgeIds }
  }

  const edgesBySource = new Map<string, Edge[]>()
  const edgesByTarget = new Map<string, Edge[]>()

  for (const edge of edges) {
    if (!edgesBySource.has(edge.source)) {
      edgesBySource.set(edge.source, [])
    }
    edgesBySource.get(edge.source)!.push(edge)

    if (!edgesByTarget.has(edge.target)) {
      edgesByTarget.set(edge.target, [])
    }
    edgesByTarget.get(edge.target)!.push(edge)
  }

  // A wire into any gate's switched-off input is off. Derived from the
  // gates' inputs (inputs[].enabled), never from a flag stored on the wire
  // (connection audit step 8, 2026-10-06): a stale `data.disabled` used to
  // prune a live branch from the run.
  const offSockets = new Set<string>()
  for (const [gateId, handles] of collectAllDisabledMergeInputs(nodes, edges)) {
    for (const handle of handles) offSockets.add(`${gateId}::${handle}`)
  }
  const isOff = (edge: Edge) => offSockets.has(`${edge.target}::${edge.targetHandle ?? ''}`)

  const enabledEdges = new Set<string>()
  const enabledNodes = new Set<string>()

  const activeEdges = edges.filter((edge) => {
    if (edge.target === mergeNodeId && disabledInputHandles.includes(edge.targetHandle || '')) {
      return false
    }
    if (isOff(edge)) {
      return false
    }
    return true
  })

  for (const edge of activeEdges) {
    enabledEdges.add(edge.id)
  }

  const mergeNode = nodes.find((n) => n.id === mergeNodeId)
  if (!mergeNode) {
    return { disabledNodeIds, disabledEdgeIds }
  }

  // Build downstream connectivity: which nodes can reach enabled sinks?
  const nodeCanReachEnabledSink = new Set<string>()

  // First, identify all sink nodes (trade/entry nodes and the merge node itself for enabled paths)
  const sinkNodes = new Set<string>()
  for (const node of nodes) {
    if (isTradePlacerType(node.type)) sinkNodes.add(node.id)
  }

  // The merge node is a sink for its ENABLED inputs only
  sinkNodes.add(mergeNodeId)

  // Trace backwards from sinks through active edges only
  function traceBackFromSink(nodeId: string, visited: Set<string>) {
    if (visited.has(nodeId)) return
    visited.add(nodeId)
    nodeCanReachEnabledSink.add(nodeId)

    const incomingEdges = edgesByTarget.get(nodeId) || []
    for (const edge of incomingEdges) {
      // Skip edges going to disabled merge inputs
      if (edge.target === mergeNodeId && disabledInputHandles.includes(edge.targetHandle || '')) {
        continue
      }
      if (isOff(edge)) {
        continue
      }
      traceBackFromSink(edge.source, visited)
    }
  }

  // Trace from all sinks
  for (const sinkId of Array.from(sinkNodes)) {
    traceBackFromSink(sinkId, new Set())
  }

  // Nodes that can reach enabled sinks are "enabled"
  for (const nodeId of Array.from(nodeCanReachEnabledSink)) {
    enabledNodes.add(nodeId)
  }

  const directlyDisabledEdges = edges.filter(
    (edge) => edge.target === mergeNodeId && disabledInputHandles.includes(edge.targetHandle || ''),
  )

  for (const edge of directlyDisabledEdges) {
    disabledEdgeIds.add(edge.id)

    checkUpstreamNode(edge.source)
  }

  function checkUpstreamNode(nodeId: string) {
    if (enabledNodes.has(nodeId)) {
      return
    }

    if (disabledNodeIds.has(nodeId)) {
      return
    }

    const outgoingEdges = edgesBySource.get(nodeId) || []

    const hasActiveOutgoing = outgoingEdges.some((edge) => {
      if (disabledEdgeIds.has(edge.id)) return false
      if (isOff(edge)) return false

      // If edge goes to the merge node's disabled input, it's not active
      if (edge.target === mergeNodeId && disabledInputHandles.includes(edge.targetHandle || '')) {
        return false
      }

      // If the target node is already disabled, this edge is not active
      if (disabledNodeIds.has(edge.target)) {
        return false
      }

      return true
    })

    if (hasActiveOutgoing) {
      return
    }

    disabledNodeIds.add(nodeId)

    for (const edge of outgoingEdges) {
      disabledEdgeIds.add(edge.id)
    }

    const incomingEdges = edgesByTarget.get(nodeId) || []
    for (const edge of incomingEdges) {
      disabledEdgeIds.add(edge.id)
      checkUpstreamNode(edge.source)
    }
  }

  return { disabledNodeIds, disabledEdgeIds }
}

export function collectAllDisabledMergeInputs(
  nodes: Node[],
  _edges: Edge[],
): Map<string, string[]> {
  const result = new Map<string, string[]>()

  for (const node of nodes) {
    if (node.type !== 'merge') continue

    const nodeData = node.data as Record<string, unknown>
    const inputs = (nodeData?.inputs || []) as Array<{ name: string; enabled?: boolean }>

    const disabledHandles: string[] = []

    for (const input of inputs) {
      if (input?.enabled === false) {
        disabledHandles.push(input.name)
      }
    }

    if (disabledHandles.length > 0) {
      result.set(node.id, disabledHandles)
    }
  }

  return result
}

/**
 * A merge is "dead" when it has at least one connected input and EVERY connected
 * input is disabled (its `data.inputs[].enabled === false`, the same predicate
 * used by collectAllDisabledMergeInputs). A merge with zero connected inputs is
 * NOT dead here — that is the run-gate's minInputs:1 concern. An input with no
 * explicit `enabled` flag is live (default-on), so it keeps the merge alive.
 */
export function findDeadMergeNodes(nodes: Node[], edges: Edge[]): DeadMergeResult {
  const deadMergeNodeIds = new Set<string>()
  const deadOutputEdgeIds = new Set<string>()
  const deadInputTargets = new Set<string>()

  const incomingByTarget = new Map<string, Edge[]>()
  const outgoingBySource = new Map<string, Edge[]>()
  for (const edge of edges) {
    if (!incomingByTarget.has(edge.target)) incomingByTarget.set(edge.target, [])
    incomingByTarget.get(edge.target)!.push(edge)
    if (!outgoingBySource.has(edge.source)) outgoingBySource.set(edge.source, [])
    outgoingBySource.get(edge.source)!.push(edge)
  }

  for (const node of nodes) {
    if (node.type !== 'merge') continue

    const incoming = incomingByTarget.get(node.id) ?? []
    if (incoming.length === 0) continue

    const inputs = ((node.data as Record<string, unknown>)?.inputs ?? []) as Array<{
      name: string
      enabled?: boolean
    }>
    const isHandleDisabled = (handle: string | null | undefined) =>
      inputs.find((i) => i?.name === handle)?.enabled === false

    if (!incoming.every((edge) => isHandleDisabled(edge.targetHandle))) continue

    deadMergeNodeIds.add(node.id)
    for (const out of outgoingBySource.get(node.id) ?? []) {
      deadOutputEdgeIds.add(out.id)
      deadInputTargets.add(`${out.target}::${out.targetHandle ?? ''}`)
    }
  }

  return { deadMergeNodeIds, deadOutputEdgeIds, deadInputTargets }
}

export function computeAllDisabledUpstream(nodes: Node[], edges: Edge[]): DisabledUpstreamResult {
  const allDisabled: DisabledUpstreamResult = {
    disabledNodeIds: new Set(),
    disabledEdgeIds: new Set(),
    ...findDeadMergeNodes(nodes, edges),
  }

  const mergeDisabledInputs = collectAllDisabledMergeInputs(nodes, edges)

  // If no merge nodes have disabled inputs, no upstream feeders to gray (the
  // dead-merge sets above are already populated and carried through).
  if (mergeDisabledInputs.size === 0) {
    return allDisabled
  }

  Array.from(mergeDisabledInputs.entries()).forEach(([mergeNodeId, disabledHandles]) => {
    const result = findDisabledUpstreamNodes(nodes, edges, mergeNodeId, disabledHandles)

    Array.from(result.disabledNodeIds).forEach((nodeId) => {
      allDisabled.disabledNodeIds.add(nodeId)
    })
    Array.from(result.disabledEdgeIds).forEach((edgeId) => {
      allDisabled.disabledEdgeIds.add(edgeId)
    })
  })

  return allDisabled
}
